import { AggregateValues, RidershipHistoryRecord } from '../../../types/ridershipTypes';

function resolveAverageByDays(sumValue = 0, dayCount = 0) {
    if (dayCount > 0) {
        return roundTo2(sumValue / dayCount);
    }

    return undefined;
}

function resolveAverageByTripsValue(
    avgByTripValue?: number | null,
    sumValue?: number | null,
    tripCount?: number | null,
) {
    return resolveAverageByTrips(sumValue, tripCount) ?? (avgByTripValue == null ? undefined : roundTo2(avgByTripValue));
}

export function projectByAggregation(rows: RidershipHistoryRecord[] | null | undefined, mode: 'sum' | 'avg' | 'avg_trips') {
    const list = rows || [];
    switch (mode) {
        case 'avg':
            return list.map(r => ({
                ...r,
                load: r.loadAvg ?? resolveAverageByDays(r.loadSum, r.dayCount),
                boarding: r.boardingAvg ?? resolveAverageByDays(r.boardingSum, r.dayCount),
                alighting: r.alightingAvg ?? resolveAverageByDays(r.alightingSum, r.dayCount),
                adjustedLoad: r.adjustedLoadAvg ?? resolveAverageByDays(r.adjustedLoadSum, r.dayCount),
                adjustedBoarding: r.adjustedBoardingAvg ?? resolveAverageByDays(r.adjustedBoardingSum, r.dayCount),
                adjustedAlighting: r.adjustedAlightingAvg ?? resolveAverageByDays(r.adjustedAlightingSum, r.dayCount),
            }));
        case 'avg_trips':
            return list.map(r => ({
                ...r,
                load: resolveAverageByTripsValue(r.loadAvgByTrip, r.loadSum, r.tripCount),
                boarding: resolveAverageByTripsValue(r.boardingAvgByTrip, r.boardingSum, r.tripCount),
                alighting: resolveAverageByTripsValue(r.alightingAvgByTrip, r.alightingSum, r.tripCount),
                adjustedLoad: resolveAverageByTripsValue(r.adjustedLoadAvgByTrip, r.adjustedLoadSum, r.tripCount),
                adjustedBoarding: resolveAverageByTripsValue(r.adjustedBoardingAvgByTrip, r.adjustedBoardingSum, r.tripCount),
                adjustedAlighting: resolveAverageByTripsValue(r.adjustedAlightingAvgByTrip, r.adjustedAlightingSum, r.tripCount),
            }));
        case 'sum':
            return list.map(r => ({
                ...r,
                load: r.loadSum,
                boarding: r.boardingSum,
                alighting: r.alightingSum,
                adjustedLoad: r.adjustedLoadSum,
                adjustedBoarding: r.adjustedBoardingSum,
                adjustedAlighting: r.adjustedAlightingSum,
            }));
        default:
            return list.map(r => ({
                ...r,
                load: r.load,
                boarding: r.boarding,
                alighting: r.alighting,
                adjustedLoad: r.adjustedLoad,
                adjustedBoarding: r.adjustedBoarding,
                adjustedAlighting: r.adjustedAlighting,
            }));
    }
}

export function projectByAggregationForAdjusted(rows: RidershipHistoryRecord[] | null | undefined, mode: 'sum' | 'avg' | 'avg_trips') {
    const list = rows || [];
    switch (mode) {
        case 'avg':
            return list.map(r => ({
                ...r,
                adjustedLoad: r.adjustedLoadAvg ?? resolveAverageByDays(r.adjustedLoadSum, r.dayCount),
                adjustedBoarding: r.adjustedBoardingAvg ?? resolveAverageByDays(r.adjustedBoardingSum, r.dayCount),
                adjustedAlighting: r.adjustedAlightingAvg ?? resolveAverageByDays(r.adjustedAlightingSum, r.dayCount),
            }));
        case 'avg_trips':
            return list.map(r => ({
                ...r,
                adjustedLoad: resolveAverageByTripsValue(r.adjustedLoadAvgByTrip, r.adjustedLoadSum, r.tripCount),
                adjustedBoarding: resolveAverageByTripsValue(r.adjustedBoardingAvgByTrip, r.adjustedBoardingSum, r.tripCount),
                adjustedAlighting: resolveAverageByTripsValue(r.adjustedAlightingAvgByTrip, r.adjustedAlightingSum, r.tripCount),
            }));
        case 'sum':
            return list.map(r => ({
                ...r,
                adjustedLoad: r.adjustedLoadSum,
                adjustedBoarding: r.adjustedBoardingSum,
                adjustedAlighting: r.adjustedAlightingSum,
            }));
        default:
            return list.map(r => ({
                ...r,
                adjustedLoad: r.adjustedLoad,
                adjustedBoarding: r.adjustedBoarding,
                adjustedAlighting: r.adjustedAlighting,
            }));
    }
}


function roundTo2(value: number) {
    return Math.round(value * 100) / 100;
}

function resolveAverageByTrips(sumValue?: number | null, tripCount?: number | null) {
    if (sumValue != null && tripCount != null && tripCount > 0) {
        return roundTo2(sumValue / tripCount);
    }

    return undefined;
}

export function toRidershipCsvInteger(value?: number | null): number | '' {
    if (value == null) {
        return '';
    }

    return Math.trunc(value);
}

export function projectAggregateMetrics<T extends AggregateValues>(rows: T[] | null | undefined, mode: 'sum' | 'avg' | 'avg_trips') {
    const list = rows || [];
    switch (mode) {
        case 'avg':
            return list.map(r => ({
                ...r,
                load: r.loadAvg ?? resolveAverageByDays(r.loadSum, r.dayCount) ?? r.load,
                boarding: r.boardingAvg ?? resolveAverageByDays(r.boardingSum, r.dayCount) ?? r.boarding,
                alighting: r.alightingAvg ?? resolveAverageByDays(r.alightingSum, r.dayCount) ?? r.alighting,
            }));
        case 'avg_trips':
            return list.map(r => ({
                ...r,
                load: resolveAverageByTripsValue(r.loadAvgByTrip, r.loadSum, r.tripCount),
                boarding: resolveAverageByTripsValue(r.boardingAvgByTrip, r.boardingSum, r.tripCount),
                alighting: resolveAverageByTripsValue(r.alightingAvgByTrip, r.alightingSum, r.tripCount),
            }));
        case 'sum':
            return list.map(r => ({
                ...r,
                load: r.loadSum ?? r.load,
                boarding: r.boardingSum ?? r.boarding,
                alighting: r.alightingSum ?? r.alighting,
            }));
        default:
            return list.map(r => ({
                ...r,
                load: r.load,
                boarding: r.boarding,
                alighting: r.alighting,
            }));
    }
}
